import React, { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { MessageSquare, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { debounce } from "lodash";
import Cookies from "js-cookie";
import toast from "react-hot-toast";

// Zustand Store
import useCanvasStore from "../stores/canvasStore";

// Services
import { getUserPreference, updateUserPreference, VIEW_TYPES } from "../services/userPreferences.service";
import { fetchFolderChildren, createGoogleDocument } from "../services/canvas.service";

// Components
import ItemColumn from "./itemColumn";
import FocusModeCommentsPanel from "./FocusModeCommentsPanel";
import ItemCreationPanel from "./ItemCreationPanel";
import FolderItem from "./folderItem";
import LinkItem from "./linkItem";
import ShortcutItem from "./shortcutItem";

// Custom resize handle component - positioned at panel edge
const ResizeHandle = ({ position = 'right', onMouseDown }) => (
    <div
        className={cn(
            "group absolute top-0 bottom-0 z-20 flex items-center justify-center cursor-col-resize",
            "w-[6px] -translate-x-1/2",
            "hover:bg-primary/10 active:bg-primary/20 transition-colors",
            position === 'right' ? "right-0 translate-x-1/2" : "left-0 -translate-x-1/2"
        )}
        onMouseDown={onMouseDown}
    >
        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-primary/60 rounded-full w-1 h-12" />
    </div>
);

/**
 * Focus Mode View - Enhanced split layout showing current column + comments panel
 * Features:
 * - Keyboard navigation (Arrow keys to navigate, Enter to select for comments)
 * - Smooth animations and transitions
 * - Better visual feedback and focus states
 * - Improved empty states
 * - Resizable panels with preference persistence
 */
const FocusModeView = () => {
    const navigate = useNavigate();
    const { folderId } = useParams();
    const containerRef = useRef(null);
    const itemColumnRef = useRef(null);

    // Panel width state - defaults will be loaded from API
    const [itemColumnWidth, setItemColumnWidth] = useState(400); // Default 40% of 1000px
    const [preferencesLoaded, setPreferencesLoaded] = useState(false);
    const preferencesLoadedRef = useRef(false);
    const [isResizing, setIsResizing] = useState(false);

    // Children section state
    const [childrenItems, setChildrenItems] = useState([]);
    const [isLoadingChildren, setIsLoadingChildren] = useState(false);
    const [newTaskOrLinkName, setNewTaskOrLinkName] = useState('');
    const [isAddingLinkOrTask, setIsAddingLinkOrTask] = useState(false);
    const [isAddingSheet, setIsAddingSheet] = useState(false);
    const [isAddingDoc, setIsAddingDoc] = useState(false);
    const [loading, setLoading] = useState(false);

    // Zustand store state
    const {
        currentDepth,
        items,
        focusedIndex,
        selectedPath,
        focusModeChildrenExpanded,
        focusModeMessagesHidden,
        openDetailPaneId,
        pasteCounter,
        setFocusMode,
        setFocusedIndex,
        setOpenDetailPaneId,
        setFocusModeChildrenExpanded,
        createFolder,
        createLink,
        fetchCanvas,
    } = useCanvasStore();

    // Get current item from URL - single source of truth
    const currentItemId = folderId ? parseInt(folderId, 10) : null;

    // Find current item in store to determine type and depth
    const currentItem = useMemo(() => {
        // Handle root case: when URL is '/' (folderId is undefined), we're at root
        // Root doesn't have a specific folder ID, but we can still navigate items at depth 1
        if (!currentItemId) {
            // At root - return null to indicate no specific item selected
            // This allows focus mode to work with root items (depth 1)
            return null;
        }

        // Search through all depths to find the item
        for (const depthKey in items) {
            const depth = parseInt(depthKey, 10);
            const itemsAtDepth = items[depth] || [];
            const foundItem = itemsAtDepth.find(item => item.id === currentItemId);
            if (foundItem) {
                return {
                    id: foundItem.id,
                    type: foundItem.item_type || 'folder',
                    depth: depth
                };
            }
        }

        // If not found, assume it's a folder (will be fetched by Home component)
        return {
            id: currentItemId,
            type: 'folder',
            depth: currentDepth
        };
    }, [currentItemId, items, currentDepth]);

    // Get items at current depth for keyboard navigation
    // When at root (currentItem is null), use depth 1 items
    const itemsAtDepth = useMemo(() => {
        const depth = currentItem?.depth ?? (currentItemId === null ? 1 : currentDepth);
        return items?.[depth] ?? [];
    }, [currentItem?.depth, currentItemId, currentDepth, items]);

    // Filter to only folders and links (items that can have comments)
    const navigableItems = useMemo(() => {
        return itemsAtDepth.filter(item =>
            item && (item.item_type === 'folder' || item.item_type === 'link')
        );
    }, [itemsAtDepth]);

    // Parse focused index for simple navigation
    const focusedNavIndex = useMemo(() => {
        if (!focusedIndex || !currentItem) return null;
        const [focusDepth, focusIdx] = focusedIndex.split('_').map(Number);
        if (focusDepth !== currentItem.depth) return null;
        if (focusIdx < 0) return null; // Skip buttons

        // Find the navigable index for this item
        const item = itemsAtDepth[focusIdx];
        if (item && (item.item_type === 'folder' || item.item_type === 'link')) {
            const navIndex = navigableItems.findIndex(navItem => navItem.id === item.id);
            return navIndex >= 0 ? navIndex : null;
        }
        return null;
    }, [focusedIndex, currentItem, itemsAtDepth, navigableItems]);

    // Get the focused item
    const focusedItem = useMemo(() => {
        if (focusedNavIndex === null || focusedNavIndex < 0 || focusedNavIndex >= navigableItems.length) {
            return null;
        }
        return navigableItems[focusedNavIndex] || null;
    }, [focusedNavIndex, navigableItems]);

    // Handle keyboard navigation in focus mode
    const handleKeyboardNavigation = useCallback((e) => {
        // Skip if typing in input
        const tag = e.target.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) {
            return;
        }

        // Allow repeats only for arrow keys
        const allowRepeat = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key);
        if (e.repeat && !allowRepeat) return;

        // At root (currentItem is null), use depth 1 for navigation
        const depth = currentItem?.depth ?? (currentItemId === null ? 1 : currentDepth);

        switch (e.key) {
            case 'Escape':
                // Check if event originated from within a dialog/modal
                // This handles cases where Radix UI Dialog handles ESC before our handler
                const target = e.target;
                const isWithinDialog = target.closest('[role="dialog"]') ||
                    target.closest('[data-radix-dialog-content]') ||
                    document.querySelector('[role="dialog"][data-state="open"]');

                // If item detail dialog is open OR event is from within a dialog, don't exit focus mode
                if (openDetailPaneId !== null || isWithinDialog) {
                    // Dialog will handle closing itself, just prevent exiting focus mode
                    // Don't prevent default or stop propagation here - let the dialog handle it
                    return;
                }

                e.preventDefault();
                e.stopPropagation();
                // Only exit focus mode if dialog is not open
                setFocusMode(false);
                break;

            case 'ArrowUp':
                e.preventDefault();
                e.stopPropagation();
                if (navigableItems.length === 0) return;

                if (focusedNavIndex !== null) {
                    // Circular navigation: if at first item (index 0), wrap to last item
                    const newIndex = focusedNavIndex > 0 ? focusedNavIndex - 1 : navigableItems.length - 1;
                    const targetItem = navigableItems[newIndex];
                    const actualIndex = itemsAtDepth.findIndex(item => item.id === targetItem.id);
                    if (actualIndex >= 0) {
                        setFocusedIndex(`${depth}_${actualIndex}`);
                    }
                } else if (navigableItems.length > 0) {
                    // Start from last item
                    const lastItem = navigableItems[navigableItems.length - 1];
                    const actualIndex = itemsAtDepth.findIndex(item => item.id === lastItem.id);
                    if (actualIndex >= 0) {
                        setFocusedIndex(`${depth}_${actualIndex}`);
                    }
                }
                break;

            case 'ArrowDown':
                e.preventDefault();
                e.stopPropagation();
                if (navigableItems.length === 0) return;

                if (focusedNavIndex !== null) {
                    // Circular navigation: if at last item, wrap to first item (index 0)
                    const newIndex = focusedNavIndex < navigableItems.length - 1 ? focusedNavIndex + 1 : 0;
                    const targetItem = navigableItems[newIndex];
                    const actualIndex = itemsAtDepth.findIndex(item => item.id === targetItem.id);
                    if (actualIndex >= 0) {
                        setFocusedIndex(`${depth}_${actualIndex}`);
                    }
                } else if (navigableItems.length > 0) {
                    // Start from first item
                    const firstItem = navigableItems[0];
                    const actualIndex = itemsAtDepth.findIndex(item => item.id === firstItem.id);
                    if (actualIndex >= 0) {
                        setFocusedIndex(`${depth}_${actualIndex}`);
                    }
                }
                break;

            case 'ArrowLeft':
                e.preventDefault();
                e.stopPropagation();
                // Navigate to parent
                if (!currentItem) {
                    // Already at root path, can't go to parent
                    return;
                }

                const currentDepth = currentItem.depth;

                // If at depth 1, we're already at root-level items, can't go to parent
                if (currentDepth === 1) {
                    return;
                }

                // Find parent from selectedPath
                // selectedPath is [parentId1, parentId2, ...] where parentId1 is at depth 1 (root-level item)
                // If currentDepth is 2, parent is at selectedPath[0] (depth 1, a root-level item)
                // If currentDepth is 3, parent is at selectedPath[1] (depth 2)
                const parentIndex = currentDepth - 2;
                if (selectedPath && selectedPath.length > parentIndex && parentIndex >= 0) {
                    const parentId = selectedPath[parentIndex];
                    // Navigate to parent folder - even if parent is a root-level item (depth 1),
                    // we still navigate to /folder/${parentId} to show its comments and children
                    navigate(`/folder/${parentId}`);
                }
                break;

            case 'Enter':
                e.preventDefault();
                e.stopPropagation();
                if (focusedItem) {
                    if (focusedItem.item_type === 'folder') {
                        // Check if children are already open (item in selectedPath)
                        if (selectedPath?.includes(focusedItem.id)) {
                            // Children already open - open item details
                            setOpenDetailPaneId(focusedItem.id);
                        } else {
                            // Navigate to the folder - updates URL and triggers fetchCanvas
                            navigate(`/folder/${focusedItem.id}`);
                        }
                    } else if (focusedItem.item_type === 'link') {
                        // For links, navigate to show comments
                        navigate(`/folder/${focusedItem.id}`);
                    }
                }
                break;
        }
    }, [currentItem, navigableItems, itemsAtDepth, focusedNavIndex, focusedItem, selectedPath, openDetailPaneId, setFocusMode, setFocusedIndex, setOpenDetailPaneId, navigate]);

    // Register keyboard handler
    useEffect(() => {
        document.addEventListener('keydown', handleKeyboardNavigation, true);
        return () => document.removeEventListener('keydown', handleKeyboardNavigation, true);
    }, [handleKeyboardNavigation]);

    // When the opened item (URL) changes, focus it and scroll it into view in the item column,
    // so the user can see which item's channel is currently open
    const scrolledToItemIdRef = useRef(null);
    useEffect(() => {
        if (!currentItemId || scrolledToItemIdRef.current === currentItemId) return;

        const actualIndex = itemsAtDepth.findIndex(item => item.id === currentItemId);
        if (actualIndex < 0) return; // Items not loaded yet - retry when they are

        setFocusedIndex(`${currentItem.depth}_${actualIndex}`);

        // Wait a frame so the item column has rendered the item before scrolling
        const frame = requestAnimationFrame(() => {
            const element = itemColumnRef.current?.querySelector(`[data-item-id="${currentItemId}"]`);
            if (!element) return;
            element.scrollIntoView({ block: 'center' });
            scrolledToItemIdRef.current = currentItemId;
        });
        return () => cancelAnimationFrame(frame);
    }, [currentItemId, currentItem?.depth, itemsAtDepth, setFocusedIndex]);

    // Auto-select first item if none selected and items exist
    useEffect(() => {
        // At root (currentItem is null) or when we have a currentItem, auto-select first item
        const depth = currentItem?.depth ?? (currentItemId === null ? 1 : currentDepth);
        if (navigableItems.length > 0 && focusedNavIndex === null) {
            // Prefer the opened item, fall back to the first navigable item
            const targetItem = navigableItems.find(item => item.id === currentItemId) || navigableItems[0];
            const actualIndex = itemsAtDepth.findIndex(item => item.id === targetItem.id);
            if (actualIndex >= 0) {
                setFocusedIndex(`${depth}_${actualIndex}`);
            }
        }
    }, [currentItem, currentItemId, currentDepth, navigableItems, itemsAtDepth, focusedNavIndex, setFocusedIndex]);

    // Fetch children when folderId from URL changes
    useEffect(() => {
        // At root (currentItem is null), don't fetch children - root items are shown in navigation
        if (!currentItem || currentItem.type !== 'folder') {
            setChildrenItems([]);
            return;
        }

        const fetchChildren = async () => {
            setIsLoadingChildren(true);
            try {
                const archiveStatus = Cookies.get('archiveStatus') || 'excludeArchive';
                const data = await fetchFolderChildren(currentItem.id, archiveStatus);

                // Combine all item types in order: shortcuts → links → folders
                const allItems = [
                    ...(data.shortcuts || []),
                    ...(data.links || []),
                    ...(data.folders || [])
                ];
                setChildrenItems(allItems);
            } catch (error) {
                console.error('Error fetching children:', error);
                toast.error('Error loading children');
                setChildrenItems([]);
            } finally {
                setIsLoadingChildren(false);
            }
        };

        fetchChildren();
    }, [currentItem?.id, currentItem?.type, pasteCounter]);

    // Item creation handlers
    const handleAddLinkOrTaskClick = useCallback(() => {
        setIsAddingLinkOrTask(true);
    }, []);

    const handleAddSheet = useCallback(() => {
        setIsAddingSheet(true);
    }, []);

    const handleAddDoc = useCallback(() => {
        setIsAddingDoc(true);
    }, []);

    const handleTaskOrLinkChange = (event) => {
        setNewTaskOrLinkName(event.target.value);
    };

    const isURL = (str) => {
        try {
            new URL(str);
            return true;
        } catch (error) {
            return false;
        }
    };

    const handleCreateNewSheet = async (name, is_sheet) => {
        try {
            return await createGoogleDocument(name, is_sheet);
        } catch (error) {
            console.log(error);
            toast.error("Error creating document");
            throw error;
        }
    };

    const handleCreateLinkOrTask = async (event) => {
        // Handle ESC key to cancel input
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setNewTaskOrLinkName('');
            setIsAddingLinkOrTask(false);
            setIsAddingSheet(false);
            setIsAddingDoc(false);
            return;
        }

        // Allow Shift+Enter for new lines in textarea
        if (event.key === 'Enter' && event.shiftKey) {
            return;
        }

        if (event.key === 'Enter' && newTaskOrLinkName.trim() !== '') {
            event.preventDefault();
            event.stopPropagation();

            const parentId = currentItem?.id || null;
            const depth = currentItem?.depth || currentDepth;

            try {
                if (isAddingSheet || isAddingDoc) {
                    // For Sheet/Doc: create, open in new tab, then close input
                    setLoading(true);
                    const url = await handleCreateNewSheet(newTaskOrLinkName, isAddingSheet);
                    await createLink(url, newTaskOrLinkName, parentId, depth + 1);
                    window.open(url, '_blank');
                    setLoading(false);
                    // Close input after sheet/doc creation
                    setNewTaskOrLinkName('');
                    setIsAddingLinkOrTask(false);
                    setIsAddingSheet(false);
                    setIsAddingDoc(false);
                } else {
                    // For Folder/Link: create and keep input open for continuous creation
                    const parts = newTaskOrLinkName.split(",");
                    if (isURL(parts[0])) {
                        await createLink(parts[0], parts[1] || null, parentId, depth + 1);
                    } else {
                        await createFolder(newTaskOrLinkName.trim(), parentId, depth + 1);
                    }
                    // Clear input but keep it open for next folder/link
                    setNewTaskOrLinkName('');
                }
            } catch (error) {
                console.error('Error creating item:', error);
                setLoading(false);
            }

            // Refresh children
            if (currentItem && currentItem.type === 'folder') {
                try {
                    const archiveStatus = Cookies.get('archiveStatus') || 'excludeArchive';
                    const data = await fetchFolderChildren(currentItem.id, archiveStatus);
                    const allItems = [
                        ...(data.shortcuts || []),
                        ...(data.links || []),
                        ...(data.folders || [])
                    ];
                    setChildrenItems(allItems);
                } catch (error) {
                    console.error('Error refreshing children:', error);
                }
            }
        }
    };

    // Load user preferences on mount
    useEffect(() => {
        const accessToken = Cookies.get('access_token');
        if (!accessToken || preferencesLoadedRef.current) return;

        const loadPreferences = async () => {
            try {
                const preferences = await getUserPreference(VIEW_TYPES.FOCUS_MODE_VIEW);
                if (preferences.itemColumnWidth) {
                    setItemColumnWidth(preferences.itemColumnWidth);
                }
                preferencesLoadedRef.current = true;
                setPreferencesLoaded(true);
            } catch (error) {
                console.error('Error loading user preferences:', error);
                preferencesLoadedRef.current = true;
                setPreferencesLoaded(true);
            }
        };

        loadPreferences();
    }, []);

    // Debounced function to save preferences
    const savePreferences = useMemo(
        () => debounce(async (data) => {
            const accessToken = Cookies.get('access_token');
            if (!accessToken || !preferencesLoadedRef.current) return;
            try {
                await updateUserPreference(VIEW_TYPES.FOCUS_MODE_VIEW, data);
            } catch (error) {
                console.error('Error saving user preferences:', error);
            }
        }, 500),
        []
    );

    // Save preferences to API when they change (debounced)
    useEffect(() => {
        if (preferencesLoadedRef.current) {
            savePreferences({
                itemColumnWidth,
            });
        }
    }, [itemColumnWidth, savePreferences]);


    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            savePreferences.cancel();
        };
    }, [savePreferences]);

    // Custom resize handler
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);

        const startX = e.clientX;
        const startItemColumnWidth = itemColumnWidth;

        const handleMouseMove = (moveEvent) => {
            if (!containerRef.current) return;
            const containerRect = containerRef.current.getBoundingClientRect();

            const newWidth = Math.min(
                Math.max(200, startItemColumnWidth + (moveEvent.clientX - startX)),
                Math.min(800, containerRect.width * 0.7)
            );
            setItemColumnWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [itemColumnWidth]);

    return (
        <div
            ref={containerRef}
            className={cn(
                "flex h-full w-full bg-background overflow-hidden",
                "animate-in zoom-in-95 fade-in duration-200 ease-out",
                isResizing && "select-none"
            )}
        >
            {/* Left Panel - Item Column */}
            <div
                className="relative h-full shrink-0 flex flex-col overflow-hidden animate-in slide-in-from-left-4 fade-in duration-300 ease-out bg-card"
                style={{
                    width: itemColumnWidth,
                    animationDelay: '50ms',
                    animationFillMode: 'backwards'
                }}
            >
                {/* Column Content - ItemColumn handles its own scrolling */}
                <div ref={itemColumnRef} className="flex-1 overflow-hidden">
                    <ItemColumn depth={currentItem?.depth ?? currentDepth} />
                </div>

                {/* Right resize handle */}
                <ResizeHandle
                    position="right"
                    onMouseDown={handleMouseDown}
                />
                <div
                    className="absolute right-0 top-0 bottom-0 w-px bg-border"
                    style={{ right: 0 }}
                />
            </div>

            {/* Right Panel - Comments */}
            <div
                className="flex-1 h-full flex flex-col overflow-hidden bg-gradient-to-br from-muted/20 via-background to-background animate-in slide-in-from-right-4 fade-in duration-300 ease-out min-w-[300px] relative"
                style={{ animationDelay: '100ms', animationFillMode: 'backwards' }}
            >
                {/* Children Section - Collapsible */}
                {/* Only show children section when we have a specific folder selected (not at root) */}
                {currentItem && currentItem.type === 'folder' && currentItemId !== null && (
                    <div className={cn(
                        "border-b border-border bg-card",
                        focusModeMessagesHidden ? "flex-1 min-h-0 flex flex-col" : "shrink-0"
                    )}>
                        {/* Header - Collapsible */}
                        <button
                            onClick={() => setFocusModeChildrenExpanded(!focusModeChildrenExpanded)}
                            className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors shrink-0"
                        >
                            <div className="flex items-center gap-2">
                                {focusModeChildrenExpanded ? (
                                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                )}
                                <span className="text-base font-medium text-foreground">
                                    Children ({childrenItems.length})
                                </span>
                            </div>
                        </button>

                        {/* Children Content - Collapsible */}
                        {focusModeChildrenExpanded && (
                            <div
                                className={cn(
                                    "border-t border-border flex flex-col",
                                    focusModeMessagesHidden ? "flex-1 min-h-0" : ""
                                )}
                                style={focusModeMessagesHidden ? {} : { maxHeight: '30vh' }}
                            >
                                {/* Item Creation Panel */}
                                <div className="shrink-0 border-b border-border">
                                    <ItemCreationPanel
                                        isAddingLinkOrTask={isAddingLinkOrTask}
                                        isAddingSheet={isAddingSheet}
                                        isAddingDoc={isAddingDoc}
                                        loading={loading}
                                        newTaskOrLinkName={newTaskOrLinkName}
                                        handleTaskOrLinkChange={handleTaskOrLinkChange}
                                        handleCreateLinkOrTask={handleCreateLinkOrTask}
                                        handleAddLinkOrTaskClick={handleAddLinkOrTaskClick}
                                        handleAddSheet={handleAddSheet}
                                        handleAddDoc={handleAddDoc}
                                        setIsAddingLinkOrTask={setIsAddingLinkOrTask}
                                        setIsAddingSheet={setIsAddingSheet}
                                        setIsAddingDoc={setIsAddingDoc}
                                        focusedButton={null}
                                        folderId={currentItem.id}
                                        isBookmarked={false}
                                        onToggleBookmark={() => { }}
                                        gdriveFolderId={null}
                                        onTsvUploadClick={() => { }}
                                    />
                                </div>

                                {/* Children Items - Scrollable */}
                                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden w-full">
                                    <div className="px-1 pt-1 space-y-0 w-full min-w-0">
                                        {isLoadingChildren ? (
                                            <div className="flex items-center justify-center py-4">
                                                <div className="text-sm text-muted-foreground">Loading...</div>
                                            </div>
                                        ) : childrenItems.length === 0 ? (
                                            <div className="flex items-center justify-center py-4">
                                                <div className="text-sm text-muted-foreground">No children</div>
                                            </div>
                                        ) : (
                                            childrenItems.map((item, index) => {
                                                if (item.item_type === 'shortcut') {
                                                    return (
                                                        <ShortcutItem
                                                            key={item.id}
                                                            id={item.id}
                                                            depth={currentItem.depth + 1}
                                                            itemIndex={index}
                                                        />
                                                    );
                                                } else if (item.item_type === 'link') {
                                                    return (
                                                        <LinkItem
                                                            key={item.id}
                                                            id={item.id}
                                                            depth={currentItem.depth + 1}
                                                            itemIndex={index}
                                                        />
                                                    );
                                                } else if (item.item_type === 'folder') {
                                                    return (
                                                        <FolderItem
                                                            key={item.id}
                                                            id={item.id}
                                                            depth={currentItem.depth + 1}
                                                            itemIndex={index}
                                                        />
                                                    );
                                                }
                                                return null;
                                            })
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Comments Panel */}
                <div className={cn(
                    "overflow-hidden relative",
                    focusModeMessagesHidden ? "shrink-0" : "flex-1"
                )}>
                    {currentItem ? (
                        <FocusModeCommentsPanel
                            key={`${currentItem.id}-${currentItem.type}`}
                            itemId={currentItem.id}
                            itemType={currentItem.type}
                            onPromoteComplete={async () => {
                                // Refresh children after promotion
                                if (currentItem && currentItem.type === 'folder') {
                                    try {
                                        // Small delay to ensure backend has processed the creation
                                        await new Promise(resolve => setTimeout(resolve, 100));

                                        const archiveStatus = Cookies.get('archiveStatus') || 'excludeArchive';
                                        const data = await fetchFolderChildren(currentItem.id, archiveStatus);
                                        const allItems = [
                                            ...(data.shortcuts || []),
                                            ...(data.links || []),
                                            ...(data.folders || [])
                                        ];
                                        // Force state update with new array reference
                                        setChildrenItems(allItems);

                                        // Also refresh canvas store to update items at current depth
                                        await fetchCanvas(currentItem.id);
                                    } catch (error) {
                                        console.error('Error refreshing children:', error);
                                    }
                                }
                            }}
                        />
                    ) : (
                        <div className="h-full flex items-center justify-center text-muted-foreground p-8">
                            <div className="text-center space-y-4 max-w-sm">
                                <div className="flex justify-center">
                                    <div className="rounded-full bg-primary/10 p-6 animate-pulse">
                                        <MessageSquare className="h-12 w-12 text-primary/60" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-foreground">
                                        {currentItemId === null ? 'At Root' : 'No item selected'}
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        {currentItemId === null
                                            ? 'Navigate items with arrow keys, then press Enter to view comments'
                                            : 'Use arrow keys to navigate items, then press Enter to view comments'
                                        }
                                    </p>
                                </div>
                                <div className="pt-4 flex items-center justify-center gap-2 text-xs">
                                    <ArrowRight className="h-4 w-4" />
                                    <span>Navigate with keyboard</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FocusModeView;
